import type { DataTableRecord, ForeignLink, JoinStrategyDirect, Model, Relation } from '../types';

interface LinkableProperty {
  key: string;
  relation?: { key: string; modelId?: string } | null;
  pk?: boolean;
}

export type ModelWithRelations = Pick<Model, 'modelId' | 'relations'>;

export interface ForeignKeyRelation {
  key: string;
  modelId: string;
  name: string;
}

export interface ExplorationLike {
  parameters: { key: string; modelId: string }[];
}

export interface LinkingProperty extends LinkableProperty {
  buildLink?: (record: DataTableRecord) => string;
  foreignLinks?: ForeignLink[];
}

export const hasDetailLink = (property: LinkingProperty) =>
  property.buildLink !== undefined || (property.foreignLinks ?? []).length > 0;

export const isExplorationReferencedByProperty = <
  T extends LinkableProperty,
  E extends ExplorationLike,
>(
  exploration: E,
  property: T,
) =>
  exploration.parameters.some(
    ({ key, modelId }) => key === property.relation?.key && modelId === property.relation?.modelId,
  );

/**
 * Derive the relations a property points to through a foreign key: when the property's source
 * model has `hasOne` relations joined on this property, the property references the related
 * models' join keys. A primary key column shadows this in `property.relation` (it always points
 * at the property's own model), so this is the only way to link e.g. a composite-key column
 * back to the models it references.
 */
export const getForeignKeyRelations = (
  property: {
    key: string;
    relation?: { key: string; modelId?: string } | null;
    model?: { modelId: string; propertyKey: string };
  },
  models: ModelWithRelations[],
): ForeignKeyRelation[] => {
  const source = property.model;
  if (source === undefined) {
    return [];
  }

  const model = models.find((m) => m.modelId === source.modelId);
  if (model === undefined) {
    return [];
  }

  return model.relations.flatMap((relation) => {
    if (
      relation.type !== 'hasOne' ||
      !isDirectJoin(relation.joinStrategy) ||
      relation.joinStrategy.joinKeyOnBase !== source.propertyKey
    ) {
      return [];
    }

    const { joinKeyOnRelated } = relation.joinStrategy;

    // The property's own relation already links to the same place (plain foreign key columns).
    if (
      relation.modelId === property.relation?.modelId &&
      joinKeyOnRelated === property.relation.key
    ) {
      return [];
    }

    return [{ modelId: relation.modelId, key: joinKeyOnRelated, name: relation.name }];
  });
};

// Steps- and SQL-based join strategies are excluded on purpose, as are direct joins going
// through an intermediary table: their base join key does not join directly onto the related
// model, so a column value cannot address the related record.
const isDirectJoin = (joinStrategy: Relation['joinStrategy']): joinStrategy is JoinStrategyDirect =>
  joinStrategy !== undefined &&
  'joinKeyOnBase' in joinStrategy &&
  joinStrategy.through === undefined;

export const getLinkableExplorations = <T extends LinkableProperty, E extends ExplorationLike>(
  properties: T[],
  explorations: E[],
) => {
  const isParameterReferencedByProperty = (parameter: ExplorationLike['parameters'][number]) =>
    properties.some(
      ({ relation }) => relation?.key === parameter.key && relation?.modelId === parameter.modelId,
    );

  const allParametersReferencedByProperties = <E extends ExplorationLike>(exploration: E) =>
    exploration.parameters.length > 0 &&
    exploration.parameters.every(isParameterReferencedByProperty);

  return explorations
    .filter(allParametersReferencedByProperties)
    .sort((a, b) => a.parameters.length - b.parameters.length);
};
